import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';
import CheckIcon from '@material-ui/icons/Check';
import cn from 'classnames';
import styles from './TapeDrawPad.styles';

const useStyles = makeStyles(styles);

const DIR_ICON = { N: ArrowUpwardIcon, S: ArrowDownwardIcon, E: ArrowForwardIcon, W: ArrowBackIcon };
const DIR_LABEL = { N: 'North', S: 'South', E: 'East', W: 'West' };

const DirButton = ({ d, current, onGo, classes }) => {
  const Icon = DIR_ICON[d];
  return (
    <Button
      size="small"
      variant="outlined"
      aria-label={DIR_LABEL[d]}
      title={DIR_LABEL[d]}
      className={cn(classes.dirButton, current === d && classes.dirButtonActive)}
      onClick={() => onGo(d)}
    >
      <Icon fontSize="small" />
    </Button>
  );
};

const TapeDrawPad = ({
  modeBar,
  dir,
  onDir,
  length,
  onLength,
  width,
  depth,
  onWidth,
  onDepth,
  onAddWall,
  onUndo,
  onRedo,
  onClose,
  canClose,
  onPlaceRect,
  onClear,
}) => {
  const classes = useStyles();

  const go = d => {
    onDir(d);
    onAddWall(d, Number(length));
  };

  return (
    <div data-ui="draw-pad" className={classes.pad}>
      <div className={classes.row}>
        <span className={classes.label}>Wall</span>
        <div className={classes.dirGrid}>
          <span />
          <DirButton d="N" current={dir} onGo={go} classes={classes} />
          <span />
          <DirButton d="W" current={dir} onGo={go} classes={classes} />
          <span />
          <DirButton d="E" current={dir} onGo={go} classes={classes} />
          <span />
          <DirButton d="S" current={dir} onGo={go} classes={classes} />
          <span />
        </div>
        <TextField
          className={classes.input}
          value={length}
          onChange={e => onLength(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onAddWall(dir, Number(length));
            }
          }}
        />
        <span className={classes.unit}>ft</span>
        <Button size="small" variant="outlined" className={classes.textButton} onClick={onUndo} startIcon={<UndoIcon fontSize="small" />}>
          Undo
        </Button>
        <Button size="small" variant="outlined" className={classes.textButton} onClick={onRedo} startIcon={<RedoIcon fontSize="small" />}>
          Redo
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!canClose}
          className={cn(classes.primaryButton, !canClose && classes.disabled)}
          onClick={onClose}
          startIcon={<CheckIcon fontSize="small" />}
        >
          Close area
        </Button>
        {modeBar && <div className={classes.modeBar}>{modeBar}</div>}
      </div>
      <div className={classes.row}>
        <span className={classes.label}>Box</span>
        <TextField className={classes.input} value={width} onChange={e => onWidth(e.target.value)} />
        <span className={classes.unit}>×</span>
        <TextField className={classes.input} value={depth} onChange={e => onDepth(e.target.value)} />
        <span className={classes.unit}>ft</span>
        <Button size="small" variant="outlined" className={classes.textButton} onClick={onPlaceRect}>
          Place rectangle
        </Button>
        <Button size="small" variant="outlined" className={classes.textButton} onClick={onClear}>
          Clear drawing
        </Button>
      </div>
    </div>
  );
};

TapeDrawPad.propTypes = {
  modeBar: PropTypes.node,
  dir: PropTypes.string,
  onDir: PropTypes.func.isRequired,
  length: PropTypes.string,
  onLength: PropTypes.func.isRequired,
  width: PropTypes.string,
  depth: PropTypes.string,
  onWidth: PropTypes.func.isRequired,
  onDepth: PropTypes.func.isRequired,
  onAddWall: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  onRedo: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  canClose: PropTypes.bool,
  onPlaceRect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default TapeDrawPad;
